import clsx from 'clsx'
import {
  shouldShowAddClassColumn,
  shouldShowAddGradeColumn,
  shouldShowAddStaffColumn,
  shouldShowAddStudentColumn,
  shouldShowAddSubjectColumn,
  shouldShowAddTeacherColumn,
  shouldShowUpdateClassColumn,
  shouldShowUpdateGradeColumn,
  shouldShowUpdateStaffColumn,
  shouldShowUpdateStudentColumn,
  shouldShowUpdateSubjectColumn,
  shouldShowUpdateTeacherColumn,
} from 'common/HelperFunctions'
import {ErrorMessage, useField} from 'formik'
import React, {FC, useCallback, useEffect, useId, useMemo, useRef, useState} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'

/**
 * CustomInputSelect Component
 *
 * A reusable form component that combines a text/number input field with a dropdown selector.
 * The input field is on the left and the dropdown is on the right, styled to match CustomSelect.
 *
 * @param {string} name - Required. Base name for the component (used for generating field names if inputName/dropdownName not provided)
 * @param {string} [inputName] - Optional. Formik field name for the input value. Defaults to `${name}_input`
 * @param {string} [dropdownName] - Optional. Formik field name for the dropdown value. Defaults to `${name}_dropdown`
 * @param {string} [label] - Optional. Label text displayed above the component
 * @param {string} [className] - Optional. Additional CSS classes for the wrapper
 * @param {boolean} [required=false] - Optional. Whether the field is required (adds required indicator)
 * @param {boolean} [showError=true] - Optional. Whether to display validation error messages
 * @param {string} [title] - Optional. Tooltip text shown when hovering over the info icon
 * @param {string} [helperText] - Optional. Helper text displayed below the component
 * @param {string} [placeholder] - Optional. Placeholder text for the input field (fallback if inputPlaceholder not provided)
 * @param {string} [inputPlaceholder] - Optional. Specific placeholder for the input field
 * @param {string} [dropdownPlaceholder] - Optional. Placeholder text shown in dropdown when no option is selected
 * @param {'text'|'number'} [inputType='text'] - Optional. Type of input field (text or number)
 * @param {DropdownOption[]} dropdownOptions - Required. Array of options for the dropdown. Each option has {label: string, value: string|number}
 * @param {boolean} [isSearchable=false] - Optional. When true, shows a search input in the dropdown menu to filter options. When false, no search bar is displayed.
 * @param {'sm'|'full'} [variant='sm'] - Optional. Controls the width of the dropdown menu:
 *   - 'sm' (default): Dropdown menu aligns to the right and matches the dropdown button width (min-width: 120px, max-width: 200px)
 *   - 'full': Dropdown menu spans the full width of the component (left: 0, right: 0)
 * @param {string} [combinedName] - Optional. Formik field name for storing the combined value (e.g., "2/day"). If provided, automatically updates this field with inputValue + separator + dropdownValue
 * @param {string} [separator='/'] - Optional. Separator used when combining input and dropdown values. Defaults to '/'
 * @param {function} [onChange] - Optional. Callback fired when either input or dropdown value changes. @note Receives (inputValue, dropdownValue)
 * @param {function} [onCombinedChange] - Optional. Callback fired when combined value changes. @note Receives (combinedString) e.g., "2/day"
 * @param {function} [onInputChange] - Optional. Callback fired when input value changes. @note Receives (value)
 * @param {function} [onDropdownChange] - Optional. Callback fired when dropdown value changes. @note Receives (value)
 * @param {boolean} [disabled=false] - Optional. Disables both input and dropdown
 * @param {boolean} [inputDisabled=false] - Optional. Disables only the input field
 * @param {boolean} [dropdownDisabled=false] - Optional. Disables only the dropdown
 * @param {string} [featureColumn] - Optional. Feature column name for feature-based visibility
 * @param {string} [featureScope] - Optional. Feature scope for feature-based visibility (e.g., 'add_class', 'update_subject')
 *
 * @returns {JSX.Element|null} The CustomInputSelect component or null if not allowed by feature flags
 *
 * @example
 * // Basic usage with text input and dropdown
 * <CustomInputSelect
 *   name="frequency"
 *   inputName="frequencyValue"
 *   dropdownName="frequencyUnit"
 *   label="Frequency"
 *   inputType="number"
 *   dropdownOptions={[
 *     { label: 'Per Day', value: 'day' },
 *     { label: 'Per Week', value: 'week' }
 *   ]}
 * />
 *
 * @example
 * // With searchable dropdown and full width variant
 * <CustomInputSelect
 *   name="duration"
 *   inputName="durationValue"
 *   dropdownName="durationUnit"
 *   inputType="number"
 *   isSearchable={true}
 *   variant="full"
 *   dropdownOptions={durationOptions}
 * />
 *
 * @example
 * // Single option - dropdown is automatically disabled
 * <CustomInputSelect
 *   name="slots"
 *   inputName="slotValue"
 *   dropdownName="slotUnit"
 *   inputType="number"
 *   dropdownOptions={[{ label: 'Slot(s)', value: 'slots' }]}
 *   // When only one option exists, dropdown is disabled and value is auto-selected
 * />
 *
 * @example
 * // With combined value - automatically creates "2/day" format
 * <CustomInputSelect
 *   name="frequency"
 *   inputName="frequencyValue"
 *   dropdownName="frequencyUnit"
 *   combinedName="frequency"  // Stores combined value like "2/day"
 *   separator="/"              // Optional, defaults to "/"
 *   inputType="number"
 *   dropdownOptions={[
 *     { label: 'Per Day', value: 'day' },
 *     { label: 'Per Week', value: 'week' }
 *   ]}
 *   onCombinedChange={(combined) => console.log(combined)} // "2/day"
 * />
 *
 * @note
 * - When dropdownOptions has only one option, the dropdown is automatically disabled and the value is auto-selected
 * - The component uses Formik's useField hook, so it must be used within a Formik context
 * - The component supports internationalization through react-intl
 */

interface DropdownOption {
  label: string
  value: string | number
}

interface Props {
  name: string
  inputName?: string
  dropdownName?: string
  label?: string
  className?: string
  required?: boolean
  showError?: boolean
  title?: string
  helperText?: string
  placeholder?: string
  inputPlaceholder?: string
  dropdownPlaceholder?: string
  inputType?: 'text' | 'number'
  dropdownOptions: DropdownOption[]
  isSearchable?: boolean
  variant?: 'sm' | 'full'
  combinedName?: string
  separator?: string
  onChange?: (inputValue: string | number, dropdownValue: string | number) => void
  onInputChange?: (value: string | number) => void
  onDropdownChange?: (value: string | number) => void
  onCombinedChange?: (combinedValue: string) => void
  disabled?: boolean
  inputDisabled?: boolean
  dropdownDisabled?: boolean
  featureColumn?: string
  featureScope?:
    | 'add_class'
    | 'update_class'
    | 'add_subject'
    | 'update_subject'
    | 'add_grade'
    | 'update_grade'
    | 'add_student'
    | 'update_student'
    | 'add_teacher'
    | 'update_teacher'
    | 'add_staff'
    | 'update_staff'
}

const CustomInputSelect: FC<Props> = ({
  name,
  inputName,
  dropdownName,
  label = '',
  className = '',
  dropdownOptions,
  required = false,
  showError = true,
  title,
  helperText,
  placeholder,
  inputPlaceholder,
  dropdownPlaceholder,
  inputType = 'text',
  isSearchable = false,
  variant = 'sm',
  combinedName,
  separator = '/',
  onChange,
  onInputChange,
  onDropdownChange,
  onCombinedChange,
  disabled = false,
  inputDisabled = false,
  dropdownDisabled = false,
  featureColumn,
  featureScope,
}) => {
  const intl = useIntl()
  const featuresString = useSelector((state: any) => state.mainReducer.generalSettings?.features)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [searchField, setSearchField] = useState('')
  const [filteredOptions, setFilteredOptions] = useState<DropdownOption[]>(dropdownOptions)

  const isAllowedByFeatures = useMemo(() => {
    if (!featureColumn || !featureScope) return true
    if (featureScope === 'add_class') return shouldShowAddClassColumn(featureColumn, featuresString)
    if (featureScope === 'update_class')
      return shouldShowUpdateClassColumn(featureColumn, featuresString)
    if (featureScope === 'add_subject')
      return shouldShowAddSubjectColumn(featureColumn, featuresString)
    if (featureScope === 'update_subject')
      return shouldShowUpdateSubjectColumn(featureColumn, featuresString)
    if (featureScope === 'add_grade') return shouldShowAddGradeColumn(featureColumn, featuresString)
    if (featureScope === 'update_grade')
      return shouldShowUpdateGradeColumn(featureColumn, featuresString)
    if (featureScope === 'add_student')
      return shouldShowAddStudentColumn(featureColumn, featuresString)
    if (featureScope === 'update_student')
      return shouldShowUpdateStudentColumn(featureColumn, featuresString)
    if (featureScope === 'add_teacher')
      return shouldShowAddTeacherColumn(featureColumn, featuresString)
    if (featureScope === 'update_teacher')
      return shouldShowUpdateTeacherColumn(featureColumn, featuresString)
    if (featureScope === 'add_staff') return shouldShowAddStaffColumn(featureColumn, featuresString)
    if (featureScope === 'update_staff')
      return shouldShowUpdateStaffColumn(featureColumn, featuresString)
    return true
  }, [featureColumn, featureScope, featuresString])

  const reactId = useId()
  const id = `${name}-${reactId}`
  const labelHasTranslation = label ? !!intl.messages[label] : false
  const placeholderHasTranslation = placeholder ? !!intl.messages[placeholder] : false
  const inputPlaceholderHasTranslation = inputPlaceholder
    ? !!intl.messages[inputPlaceholder]
    : false
  const dropdownPlaceholderHasTranslation = dropdownPlaceholder
    ? !!intl.messages[dropdownPlaceholder]
    : false
  const titleHasTranslation = title ? !!intl.messages[title] : false
  const helperTextHasTranslation = helperText ? !!intl.messages[helperText] : false

  // Use separate fields for input and dropdown, or a combined field
  const actualInputName = inputName || `${name}_input`
  const actualDropdownName = dropdownName || `${name}_dropdown`

  const [inputField, inputMeta, inputHelpers] = useField(actualInputName)
  const [dropdownField, dropdownMeta, dropdownHelpers] = useField(actualDropdownName)
  // Use combined field if combinedName is provided, otherwise use a dummy field name
  const [, , combinedHelpers] = useField(combinedName || `${name}_combined`)

  const hasSingleOption = useMemo(() => {
    return dropdownOptions.length === 1
  }, [dropdownOptions])

  const selectedOption = useMemo(() => {
    return dropdownOptions.find((opt) => opt.value == dropdownField.value)
  }, [dropdownOptions, dropdownField.value])

  const renderTooltip = useMemo(() => {
    return (
      <Tooltip id={`tooltip-${id}`}>
        {titleHasTranslation ? intl.formatMessage({id: title}) : title}
      </Tooltip>
    )
  }, [title, id, intl, titleHasTranslation])

  // Update combined value when input or dropdown changes
  const updateCombinedValue = useCallback(
    (inputVal: string | number, dropdownVal: string | number) => {
      if (combinedName) {
        const inputStr =
          inputVal !== '' && inputVal !== null && inputVal !== undefined ? String(inputVal) : ''
        const dropdownStr =
          dropdownVal !== '' && dropdownVal !== null && dropdownVal !== undefined
            ? String(dropdownVal)
            : ''
        const combined = inputStr && dropdownStr ? `${inputStr}${separator}${dropdownStr}` : ''
        if (combinedHelpers) {
          combinedHelpers.setValue(combined)
        }
        if (onCombinedChange) onCombinedChange(combined)
      }
    },
    [combinedName, separator, combinedHelpers, onCombinedChange]
  )

  // Auto-set dropdown value when there's only one option and close dropdown
  useEffect(() => {
    if (hasSingleOption && dropdownOptions.length > 0) {
      const singleOption = dropdownOptions[0]
      if (dropdownField.value !== singleOption.value) {
        dropdownHelpers.setValue(singleOption.value)
        if (onDropdownChange) onDropdownChange(singleOption.value)
        if (onChange) onChange(inputField.value, singleOption.value)
        updateCombinedValue(inputField.value, singleOption.value)
      }
      // Close dropdown if it's open when there's only one option
      if (isDropdownOpen) {
        setIsDropdownOpen(false)
        setSearchField('')
      }
    }
  }, [
    hasSingleOption,
    dropdownOptions,
    dropdownField.value,
    dropdownHelpers,
    onChange,
    onDropdownChange,
    inputField.value,
    isDropdownOpen,
    updateCombinedValue,
  ])

  // Filter options based on search (only when searchable)
  useEffect(() => {
    if (isSearchable && searchField) {
      // Options built from API rows can carry a null label or value
      const needle = searchField.toLowerCase()
      const matches = (fieldValue: any) =>
        fieldValue !== null &&
        fieldValue !== undefined &&
        String(fieldValue).toLowerCase().includes(needle)
      const filtered = dropdownOptions.filter((opt) => matches(opt.label) || matches(opt.value))
      setFilteredOptions(filtered)
    } else {
      setFilteredOptions(dropdownOptions)
    }
  }, [searchField, dropdownOptions, isSearchable])

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
        setSearchField('')
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen])

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let value: string | number = e.target.value
      if (inputType === 'number') {
        value = e.target.value === '' ? '' : Number(e.target.value)
      }
      inputHelpers.setValue(value)
      // Mark field as touched on change so validation errors (e.g. max limits)
      // appear immediately instead of only after blur.
      inputHelpers.setTouched(true, false)
      if (onInputChange) onInputChange(value)
      if (onChange) onChange(value, dropdownField.value)
      updateCombinedValue(value, dropdownField.value)
    },
    [inputType, inputHelpers, onInputChange, onChange, dropdownField.value, updateCombinedValue]
  )

  const handleDropdownSelect = useCallback(
    (option: DropdownOption) => {
      dropdownHelpers.setValue(option.value)
      setIsDropdownOpen(false)
      setSearchField('')
      if (onDropdownChange) onDropdownChange(option.value)
      if (onChange) onChange(inputField.value, option.value)
      updateCombinedValue(inputField.value, option.value)
    },
    [dropdownHelpers, onDropdownChange, onChange, inputField.value, updateCombinedValue]
  )

  const handleDropdownToggle = useCallback(() => {
    if (!disabled && !dropdownDisabled && !hasSingleOption) {
      setIsDropdownOpen((prev) => !prev)
    }
  }, [disabled, dropdownDisabled, hasSingleOption])

  const hasError =
    (inputMeta.touched && inputMeta.error) || (dropdownMeta.touched && dropdownMeta.error)

  return isAllowedByFeatures ? (
    <div className={clsx('mb-3', className)}>
      {label && (
        <label className={clsx('form-label', {required})} htmlFor={id}>
          {labelHasTranslation ? intl.formatMessage({id: label}) : label}
          {title && (
            <OverlayTrigger placement='right' overlay={renderTooltip}>
              <div className='badge pe-auto'>
                <i className='bi bi-info-circle-fill'></i>
              </div>
            </OverlayTrigger>
          )}
        </label>
      )}

      <div
        className={clsx('custom-input-select-wrapper', `custom-input-select-variant-${variant}`)}
        ref={dropdownRef}
      >
        <div
          className={clsx('custom-input-select-control', {
            'is-invalid': hasError,
            'is-disabled': disabled,
          })}
        >
          {/* Input Field */}
          <input
            id={id}
            type={inputType}
            className='custom-input-select-input'
            value={inputField.value ?? ''}
            onChange={handleInputChange}
            onBlur={inputField.onBlur}
            disabled={disabled || inputDisabled}
            placeholder={
              inputPlaceholder
                ? inputPlaceholderHasTranslation
                  ? intl.formatMessage({id: inputPlaceholder})
                  : inputPlaceholder
                : placeholderHasTranslation
                  ? intl.formatMessage({id: placeholder})
                  : placeholder || 'Enter value'
            }
          />

          {/* Dropdown Button */}
          <div
            className={clsx('custom-input-select-dropdown', {
              'is-open': isDropdownOpen,
              'is-disabled': disabled || dropdownDisabled || hasSingleOption,
            })}
            onClick={handleDropdownToggle}
          >
            <div className='custom-input-select-dropdown-value'>
              {selectedOption?.label ||
                (hasSingleOption && dropdownOptions.length > 0
                  ? dropdownOptions[0].label
                  : dropdownPlaceholder
                    ? dropdownPlaceholderHasTranslation
                      ? intl.formatMessage({id: dropdownPlaceholder})
                      : dropdownPlaceholder
                    : 'Select')}
            </div>
            {!hasSingleOption && (
              <div className='custom-input-select-dropdown-arrow'>
                <i className={clsx('bi', isDropdownOpen ? 'bi-chevron-up' : 'bi-chevron-down')}></i>
              </div>
            )}
          </div>
        </div>

        {/* Dropdown Menu - Only show when there's more than one option */}
        {isDropdownOpen && !hasSingleOption && (
          <div className={clsx('custom-input-select-menu', `custom-input-select-menu-${variant}`)}>
            {/* Search Input - Only show when searchable */}
            {isSearchable && (
              <div className='custom-input-select-menu-search'>
                <input
                  type='text'
                  className='form-control form-control-sm'
                  placeholder='Search...'
                  value={searchField}
                  onChange={(e) => setSearchField(e.target.value)}
                  autoFocus
                />
              </div>
            )}

            {/* Options List */}
            <div className='custom-input-select-menu-list'>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className={clsx('custom-input-select-menu-option', {
                      'is-selected': option.value === dropdownField.value,
                    })}
                    onClick={() => handleDropdownSelect(option)}
                  >
                    {option.label}
                  </div>
                ))
              ) : (
                <div className='custom-input-select-menu-no-options'>No options found</div>
              )}
            </div>
          </div>
        )}
      </div>

      {helperText && (
        <small id={`${id}-help-text`} className='form-text text-muted fst-italic'>
          {helperTextHasTranslation ? intl.formatMessage({id: helperText}) : helperText}
        </small>
      )}
      {showError && (
        <>
          <ErrorMessage
            component='div'
            name={actualInputName}
            className='invalid-feedback mb-3 d-block'
          />
          <ErrorMessage
            component='div'
            name={actualDropdownName}
            className='invalid-feedback mb-3 d-block'
          />
        </>
      )}
    </div>
  ) : null
}

export default CustomInputSelect
